import { Dialog, DialogContent, Grid, InputAdornment, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppTransComponents } from "app/appTrans";
import { MprcDialogTitleClose, MprcRequestIndicator } from "components";
import { MprcFilterBoxContent } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcFilterNoRecords } from "components/filter/MprcFilterNoRecords";
import { IconButtonCancel } from "components/iconButtons";
import { IconAdd, IconSearch } from "components/icons";
import { IconUserRoleDynamic } from "components/icons/IconUserRoleDynamic";
import { AutoCompleteDataModel, AutoCompleteSuggestions, MprcAutoComplete } from "components/MprcAutoComplete";
import { MprcBarTitle } from "components/MprcBarTitle";
import { MprcButton } from "components/MprcButton";
import { MprcConfirmDialog } from "components/MprcConfirmDialog";
import { MprcContainer } from "components/MprcContainer";
import { MprcDropdownBuildGroupedItems, MprcDropdownItem, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcIconButtonMoreOptionsDropdown } from "components/MprcIconButtonMoreOptionsDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcRequestErrorDialog } from "components/MprcRequestErrorDialog";
import { MprcText } from "components/MprcText";
import { MprcTextFieldColor } from "components/MprcTextFieldColor";
import { GetRoleSuggestionsPayload, ManagedRoleUserStatus, ManagedUserRolesQueryResponse, ManagedUserRolesQueryResponsePaginatedList, UserType } from "generated/CoreApi_v1";
import { TFunction } from "i18next";
import { NUMBER_MAX_INT32 } from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { UserGroupsAssignedDrawer } from "views/foundation/security/drawers/UserGroupsAssignedDrawer";
import { EditRoleUserTabs } from "views/foundation/security/Home/EditRole/EditRoleUserTabs";
import { UserPermissionListDrawer } from "views/foundation/user/drawers/UserPermissionListDrawer";
import { EditUserHeader } from "views/foundation/user/Home/EditUser/EditUserHeader";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
    userName: string;
    userType: UserType;
    viewMode?: "dialog" | "form";
    onClose: () => void;
}

type FormMode = "adding" | "filtering";

type RoleMenuOptions = "remove" | "constraints" | "blockDynamicPermissions" | "unblockDynamicPermissions" | "viewDynamicPermissions";

////////////////////////////////////////////////////////////////////////////

///
function getRoleMenuItems(item: ManagedUserRolesQueryResponse, userType: UserType, t: TFunction): MprcDropdownItem<RoleMenuOptions>[] {

    if (!item.canChangeUserOptions)
        return [];

    var group0: MprcDropdownItems<RoleMenuOptions> = [];
    if (!item.isReserved)
        group0.push({ text: t("User.Edit.Roles.EditContraintsAction"), value: "constraints" });

    var group1: MprcDropdownItems<RoleMenuOptions> = [];
    if (!item.isDirectAssignment) {
        group1.push({ text: t("User.Edit.Roles.ViewDynamicPermissionsAction"), value: "viewDynamicPermissions" });

        if (item.statusCode == ManagedRoleUserStatus.Blocked)
            group1.push({ text: t("User.Edit.Roles.ReactivateAction"), value: "unblockDynamicPermissions" });
        else
            group1.push({ text: <MprcText color="error">{t("User.Edit.Roles.BlockAction")}</MprcText>, value: "blockDynamicPermissions" });
    }
    else {
        if (userType != UserType.Admin || !item.isReserved) {
            group1.push({ text: <MprcText color="error">{t("User.Edit.Roles.RemoveAction")}</MprcText>, value: "remove" });
        }
    }

    return MprcDropdownBuildGroupedItems<RoleMenuOptions>([group0, group1]);
}

/**
 * Edição das Permissões de um Papel Segurança
 * @param props
 */
export const EditUserRoles: React.FC<IProps> = (props) => {
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    ////////////////////////////////////////////////////////////////////////////
    const { userId, userName, userType, viewMode, onClose } = props;

    const [loading, setLoading] = useState<boolean>(true);
    const [adding, setAddRole] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [formMode, setFormMode] = useState<FormMode>("adding");

    const [dialog, setDialog] = useState<any | null>(null);
    const [viewError, setViewError] = useState<any>();
    const [canAdd, setCanAdd] = useState<boolean | undefined>(undefined);

    const [viewData, setViewData] = useState<Nullable<ManagedUserRolesQueryResponsePaginatedList>>(null);
    const [blockRole, setBlockRole] = useState<{ roleId: string, status: "desactivate" | "reactivate"; } | null>(null);

    const searchRef = useRef<HTMLInputElement>(undefined);

    const hasRoles = useRef<boolean>(false);

    const filtered = searchText.length > 0;

    ////////////////////////////////////////////////////////////////////////////

    ///
    const refreshData = (page: number) => {
        setLoading(true);
    };

    ///
    const handleCancelSearch = () => {
        setSearchText("");
        refreshData(1);
    };

    ///
    const handleOption = (item: ManagedUserRolesQueryResponse, option: RoleMenuOptions) => {
        switch (option) {

            case "remove":
                setDialog(<MprcConfirmDialog
                    open
                    severity="danger"
                    message={
                        <Trans
                            i18nKey="User.Edit.Roles.ConfirmDeleteMessage"
                            values={{ name: item.name }}
                            components={AppTransComponents}
                        />
                    }
                    onClose={
                        (result) => {
                            if (result) {
                                setDeleting(item.roleId);
                            }
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "blockDynamicPermissions":
                setDialog(<MprcConfirmDialog
                    open
                    severity="danger"
                    message={
                        <Trans
                            i18nKey="User.Edit.Roles.ConfirmBlockMessage"
                            values={{ name: item.name }}
                            components={AppTransComponents}
                        />
                    }
                    onClose={
                        (result) => {
                            if (result) {
                                setBlockRole({ roleId: item.roleId, status: "desactivate" });
                            }
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "unblockDynamicPermissions":
                setBlockRole({ roleId: item.roleId, status: "reactivate" });
                break;

            case "constraints":
                setDialog(<EditRoleUserTabs
                    roleId={item.roleId}
                    roleName={item.name}
                    isReserved={item.isReserved}
                    userId={userId}
                    userName={userName}
                    onClose={
                        (reload) => {
                            if (reload) setLoading(true);
                            setDialog(null);
                        }
                    }
                />);
                break;

            case "viewDynamicPermissions":
                setDialog(
                    <UserGroupsAssignedDrawer
                        userId={userId}
                        subtitle={userName}
                        onClose={() => setDialog(null)}
                    />
                );
                break;
        }
    };

    ///
    const getRoleSuggestions = (searchText: string, pageNumber: number, pageSize: number): Promise<AutoCompleteSuggestions<AutoCompleteDataModel>> => {
        searchText = searchText.toLowerCase();

        const params: GetRoleSuggestionsPayload = {
            userId,
            searchText,
            pageNumber,
            pageSize
        };

        return new Promise((resolve) => {
            api.v1.userManagement.getRoleSuggestions(params)
                .then(result => {
                    resolve({
                        items: result.data.items as AutoCompleteDataModel[],
                        totalRows: result.data.totalRows
                    });
                })
                .catch(e => {
                    resolve({ items: [], totalRows: 0 });
                    setViewError(e);
                });
        });
    };

    ///
    const handleAddRole = (items: AutoCompleteDataModel[]) => {
        if (items) setAddRole(items[0].key);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const RenderFilter = (
        <MprcContainer>
            <MprcCardFilterBox>
                <MprcFilterBoxContent
                    title={t("User.Edit.Roles.Title")}
                    filterAction={
                        {
                            readonly: false,
                            filtered: formMode == "filtering",
                            onClick: (e) => setFormMode(formMode == "filtering" ? "adding" : "filtering")
                        }
                    }
                >
                    <Grid container justifyContent={"space-between"}>
                        {
                            formMode == "filtering"
                                ? (
                                    <Grid size="grow" sx={(theme) => ({ padding: theme.spacing(1) })}>
                                        <MprcTextFieldColor
                                            autoFocus
                                            fullWidth
                                            placeholder={t("User.Edit.Roles.SearchTextPlaceholder")}
                                            inputColor="warning"
                                            underlineColor="warning"
                                            value={searchText}
                                            inputRef={(ref) => searchRef.current = ref}
                                            onChange={(e) => setSearchText(e.currentTarget.value)}
                                            onKeyDown={(e) => { if (e.keyCode == 13) refreshData(1); }}
                                            endAdornment={
                                                searchText == ""
                                                    ? null
                                                    : <IconButtonCancel color="warning" size="small" onClick={handleCancelSearch} />
                                            }
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <IconSearch fontSize="inherit" />
                                                </InputAdornment>
                                            }
                                        />
                                    </Grid>
                                )
                                : (
                                    <Grid size="grow">
                                        <Grid container>
                                            <Grid size="grow">
                                                <MprcAutoComplete
                                                    id="rolesInput"
                                                    placeholder={canAdd == undefined || canAdd == true ? t("User.Edit.Roles.AddRolePlaceholder") : t("User.Edit.Roles.AddRoleNotAllowedPlaceholder")}
                                                    autoFocus
                                                    disabled={!canAdd}
                                                    selectedItems={[]}
                                                    onGetSuggestions={getRoleSuggestions}
                                                    onChange={handleAddRole}
                                                    multiSelect={false}
                                                    startAdornment={
                                                        <InputAdornment position="start" key="AutoCompleteAdorment">
                                                            <IconAdd fontSize="inherit" />
                                                        </InputAdornment>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )
                        }
                    </Grid>
                </MprcFilterBoxContent>
            </MprcCardFilterBox>
        </MprcContainer>
    );

    ///
    const RenderGrid = (
        <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={1}
        >
            {
                viewData?.items.map((item, index) => {
                    const menuItems = getRoleMenuItems(item, userType, t);
                    return (
                        <Grid key={index} size={{ xs: 12 }} width="100%">
                            <Grid
                                container
                                justifyContent="center"
                                alignItems="center"
                            >

                                <Grid size="grow" overflow="hidden">
                                    <Grid container alignItems="center" flexWrap="nowrap" width="100%">
                                        <Grid overflow="hidden">
                                            <MprcListItemTitle
                                                highlighted={item.statusCode == ManagedRoleUserStatus.Blocked}
                                                highlightedColor="error"
                                                onClick={() => handleOption(item, "constraints")}
                                            >
                                                {item.name}
                                            </MprcListItemTitle>
                                        </Grid>
                                        {menuItems.length > 0 &&
                                            <Grid>
                                                <MprcIconButtonMoreOptionsDropdown
                                                    color="muted"
                                                    onChange={(selectedOption) => handleOption(item, selectedOption)}
                                                    items={menuItems}
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                    <Grid container alignItems="center" spacing={1} flexWrap="nowrap" width="100%">
                                        <Grid sx={(theme) => ({ mt: menuItems.length > 0 ? theme.spacing(-.5) : undefined })}>
                                            <Typography
                                                variant="caption"
                                                color={
                                                    item.statusCode == ManagedRoleUserStatus.Blocked
                                                        ? "error.main"
                                                        : item.statusCode == ManagedRoleUserStatus.Custom
                                                            ? "warning.main"
                                                            : "muted.main"
                                                }
                                            >
                                                {!item.isDirectAssignment && <IconUserRoleDynamic title={t("User.Edit.Roles.DynamicAssignTooltip")} fixedWidth />}
                                                {item.statusCodeName}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    );
                })
            }
        </Grid>
    );

    const RenderContent = (
        <>
            {dialog}

            <MprcRequestIndicator
                visible={adding != null || deleting != null || blockRole != null || loading}
                type={loading ? "request" : "post"} />

            <MprcRequestErrorDialog
                error={viewError}
                onClose={() => { setViewError(undefined); }}
            />

            <Grid container direction="column" flexWrap="nowrap" height="100%" spacing={0} className={AppGlobalClassKeys.containerRounded}>
                <Grid>
                    {RenderFilter}
                </Grid>
                <Grid overflow="auto" padding={1}>
                    {
                        viewData?.items.length == 0
                            ? <MprcFilterNoRecords
                                filtered={filtered}
                                message={t("User.Edit.Roles.NoRecordsFound")}
                                emptyDatasetMessage={t("User.Edit.Roles.ListEmptyMessage")}
                                onResetFilter={handleCancelSearch}
                            />
                            : RenderGrid
                    }
                </Grid>
                {
                    hasRoles.current &&
                    <Grid alignSelf="center" padding={1}>
                        <MprcButton
                            variant="contained"
                            rounded
                            onClick={
                                () => setDialog(<UserPermissionListDrawer
                                    userId={userId}
                                    subtitle={userName}
                                    onClose={() => setDialog(null)} />
                                )
                            }
                            text={t("User.Edit.Roles.PermissionListButtonText")}
                        />
                    </Grid>
                }
            </Grid>
        </>
    );

    ////////////////////////////////////////////////////////////////////////////////
    /// Effect's
    ////////////////////////////////////////////////////////////////////////////////

    // Ao carregar...
    useEffect(() => {
        ///
        async function getData() {
            try {
                var result = await api.v1.userManagement.getRoles({
                    userId: userId,
                    searchText: searchText,
                    pageNumber: 1,
                    pageSize: NUMBER_MAX_INT32
                });

                setViewData(result.data);
                searchRef.current?.focus();

                if (searchText == null || searchText == "")
                    hasRoles.current = result.data.totalRows > 0;
            }
            catch (e: any) {
                setViewData(null);
                setViewError(e as Error);
            }
            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);


    // Ao adicionar...
    useEffect(() => {
        async function postData() {
            if (adding) {
                try {
                    await api.v1.userManagement.addRole({ userId, roleId: adding });
                    setLoading(true);
                }
                catch (e) {
                    setViewError(e);
                }

                setAddRole(null);
            }
        }

        postData();

    }, [adding]);

    // Ao remover...
    useEffect(() => {
        async function postData() {

            if (deleting) {
                try {
                    if (await api.v1.userManagement.deleteRole({ userId, roleId: deleting })) {
                        enqueueSnackbar(t("User.Edit.Roles.OperationSuccessMessage"), { variant: "success" });
                        setLoading(true);
                    }
                    else
                        enqueueSnackbar(t("User.Edit.Roles.OperationFailMessage"), { variant: "error" });

                }
                catch (e) {
                    setViewError(e);
                }
            }

            setDeleting(null);
        }

        postData();

    }, [deleting]);

    // Ao bloquear...
    useEffect(() => {
        async function postData() {

            if (blockRole) {
                try {
                    const payload = {
                        userId: userId,
                        roleId: blockRole.roleId,
                    };

                    const result = (blockRole.status == "desactivate")
                        ? await api.v1.userManagement.desactivateRole(payload)
                        : await api.v1.userManagement.reactivateRole(payload);

                    if (result) enqueueSnackbar(t("User.Edit.Roles.OperationSuccessMessage"), { variant: "success" });
                    else if (result) enqueueSnackbar(t("User.Edit.Roles.OperationFailMessage"), { variant: "error" });

                    setLoading(true);
                }
                catch (e) {
                    setViewError(e);
                }
            }

            setBlockRole(null);
        }
        postData();

    }, [blockRole]);

    useEffect(() => {
        api.v1.userManagement.getRolePermissions({ userId })
            .then(({ data: { canAssignRoles } }) => setCanAdd(canAssignRoles));
    }, []);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return viewMode == "dialog"
        ? (
            <Dialog
                open
                fullWidth
                maxWidth="lg"
            >
                <MprcDialogTitleClose
                    title={userName}
                    onClose={onClose}
                />
                <DialogContent sx={{ height: "100vh" }} >
                    {RenderContent}
                </DialogContent>
            </Dialog>
        )
        : (
            <Grid container direction="column" flexWrap="nowrap" spacing={1} height="100%">
                <Grid>
                    <EditUserHeader
                        onClose={onClose}
                        recordChanged={false}
                        reloadOnClose={false}
                        actions={[<MprcBarTitle>{userName}</MprcBarTitle>]}
                    />
                </Grid>

                <Grid size="grow" height="100%">
                    {RenderContent}
                </Grid>
            </Grid>
        );
};
